import { useState, useEffect, useMemo } from "react";
import { authFetch } from "../api";
import Header from "../components/Header";
import InvestorOnboardingBox from "../components/InvestorOnboardingBox";
import { emptyMember } from "../components/MembersEditor";
import { ONBOARDING_SCHEMA } from "../config/investorOnboardingSchema";

/**
 * Investor onboarding — the three-section signup that turns a prospective investor
 * into a matchable profile. Config-driven from investorOnboardingSchema:
 * Section (step) → Box (sub-section) → Field.
 *
 * §1 saves on its own (creates the investor draft + writes members); §2/§3 upsert
 * against it; Finish persists all three.
 */

const MULTI_TYPES = new Set(["multiselect", "boroughs", "neighborhoods", "buildings"]);

// Build empty form state: members start with one primary row, array fields [], rest "".
function buildInitialForm() {
  const form = {};
  ONBOARDING_SCHEMA.forEach((s) =>
    s.boxes.forEach((b) =>
      b.fields.forEach((f) => {
        form[f.key] = f.type === "members" ? [emptyMember(true)] : MULTI_TYPES.has(f.type) ? [] : "";
      })
    )
  );
  return form;
}

const isEmpty = (v) => (Array.isArray(v) ? v.length === 0 : v == null || v === "");
// A members field is "empty" until at least one member has a first name.
const isFieldEmpty = (f, v) =>
  f.type === "members" ? !Array.isArray(v) || !v.some((m) => (m.first_name || "").trim()) : isEmpty(v);

export default function InvestorOnboardingPage() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(buildInitialForm);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [investorId, setInvestorId] = useState(null); // set once Section 1 is saved (draft)
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    // Neighborhood picker options for Section 2.
    authFetch("/buildings/neighborhoods")
      .then((r) => setNeighborhoods(r?.neighborhoods || []))
      .catch(() => setNeighborhoods([]));
    // Team-member list for the "assigned team member" picker (all authenticated users).
    authFetch("/investors/meta/team-members")
      .then((r) => setTeamMembers(r?.team_members || []))
      .catch(() => setTeamMembers([]));
  }, []);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const section = ONBOARDING_SCHEMA[step];

  // Required fields missing, per section.
  const missingBySection = useMemo(
    () =>
      ONBOARDING_SCHEMA.map((s) =>
        s.boxes.flatMap((b) => b.fields).filter((f) => f.required && isFieldEmpty(f, form[f.key]))
      ),
    [form]
  );
  const sectionMissing = missingBySection[step];

  const goTo = (i) => {
    setShowErrors(false);
    setStep(i);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Flat form → just one section's field keys.
  const buildSectionPayload = (sectionId) => {
    const s = ONBOARDING_SCHEMA.find((x) => x.id === sectionId);
    const out = {};
    s.boxes.forEach((b) => b.fields.forEach((f) => { out[f.key] = form[f.key]; }));
    return out;
  };

  // Persist Section 1: create the investor on first save, update it thereafter.
  // Returns {ok, id} / {ok:false, error}. (authFetch resolves JSON on any status, so we
  // check the shape: {id,...} on success, {detail} on error.) Returns the id so callers
  // can chain the §2 save without waiting for the investorId state to flush.
  const persistProfile = async () => {
    const payload = buildSectionPayload("profile");
    const members = Array.isArray(payload.members) ? payload.members : [];
    delete payload.members; // members live in a sub-table, saved separately below
    const res = await authFetch(investorId ? `/investors/${investorId}` : "/investors", {
      method: investorId ? "PATCH" : "POST",
      body: JSON.stringify(payload),
    });
    if (!(res && res.id)) {
      return { ok: false, error: (res && res.detail) || "Save failed — check the server is running." };
    }
    setInvestorId(res.id);
    // Write the household members to investor_members (replace-all).
    const mres = await authFetch(`/investors/${res.id}/members`, {
      method: "PUT",
      body: JSON.stringify({ members }),
    });
    if (!(mres && Array.isArray(mres.members))) {
      return { ok: false, error: (mres && mres.detail) || "Investor saved, but members failed to save." };
    }
    return { ok: true, id: res.id };
  };

  // Persist Section 2 (search criteria) for a given investor id — upserts the active row.
  const persistSearch = async (id) => {
    const iid = id || investorId;
    if (!iid) return { ok: false, error: "Save Section 1 first — the search criteria attach to the investor." };
    const res = await authFetch(`/investors/${iid}/search-criteria`, {
      method: "PUT",
      body: JSON.stringify(buildSectionPayload("search")),
    });
    if (res && res.id) return { ok: true };
    return { ok: false, error: (res && res.detail) || "Save failed — check the server is running." };
  };

  // Persist Section 3 (CRM) for a given investor id — upserts the 1:1 row.
  const persistCrm = async (id) => {
    const iid = id || investorId;
    if (!iid) return { ok: false, error: "Save Section 1 first — CRM attaches to the investor." };
    const res = await authFetch(`/investors/${iid}/crm`, {
      method: "PUT",
      body: JSON.stringify(buildSectionPayload("internal")),
    });
    if (res && res.id) return { ok: true };
    return { ok: false, error: (res && res.detail) || "Save failed — check the server is running." };
  };

  const saveProfile = async (advance) => {
    if (missingBySection[0].length > 0) {
      setShowErrors(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setSaving(true);
    setSaveError(null);
    const { ok, error } = await persistProfile();
    setSaving(false);
    if (!ok) { setSaveError(error); return; }
    setSaved(true);
    if (advance) goTo(1);
  };

  const saveSearch = async (advance) => {
    if (!investorId) { setSaveError("Save Section 1 first — the search criteria attach to the investor."); return; }
    if (missingBySection[1].length > 0) {
      setShowErrors(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setSaving(true);
    setSaveError(null);
    const { ok, error } = await persistSearch(investorId);
    setSaving(false);
    if (!ok) { setSaveError(error); return; }
    setSaved(true);
    if (advance) goTo(2);
  };

  const handleNext = () => {
    if (step === 0) { saveProfile(true); return; }   // §1 saves on the way forward
    if (step === 1) { saveSearch(true); return; }    // §2 saves on the way forward
    goTo(step + 1);
  };

  const handleSubmit = async () => {
    // Validate everything and jump to the first incomplete section.
    if (missingBySection.flat().length > 0) {
      setShowErrors(true);
      const firstBad = missingBySection.findIndex((m) => m.length > 0);
      if (firstBad !== -1) goTo(firstBad);
      return;
    }
    setSaving(true);
    setSaveError(null);
    // §1 (parent), then §2 — thread the id so §2 doesn't race the state update.
    let id = investorId;
    if (!id) {
      const p = await persistProfile();
      if (!p.ok) { setSaving(false); setSaveError(p.error); goTo(0); return; }
      id = p.id;
    }
    const s = await persistSearch(id);
    if (!s.ok) { setSaving(false); setSaveError(s.error); return; }
    const c = await persistCrm(id);
    setSaving(false);
    if (!c.ok) { setSaveError(c.error); return; }
    setSaved(true);
  };

  const isLast = step === ONBOARDING_SCHEMA.length - 1;

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f4f6f8" }}>
      <Header />

      <div style={{ maxWidth: "920px", margin: "0 auto", padding: "28px 24px 80px" }}>
        {/* Title */}
        <div style={{ marginBottom: "20px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <h1 style={{ margin: "0 0 6px", fontSize: "26px", fontWeight: 700, color: "#161b22" }}>New Investor</h1>
            {investorId && (
              <span style={{ fontSize: "12.5px", fontWeight: 600, color: "#2e7d46", backgroundColor: "#e6f4ea", border: "1px solid #b7dfc4", borderRadius: "16px", padding: "3px 11px" }}>
                Draft saved · Investor #{investorId}
              </span>
            )}
          </div>
          <p style={{ margin: 0, color: "#6b7480", fontSize: "14px" }}>
            Onboard a prospective investor across three sections. Fields marked <span style={{ color: "#c0392b" }}>*</span> are required; everything else is optional under “Advanced”. Section 1 saves on its own; Sections 2 & 3 are next.
          </p>
        </div>

        {/* Stepper */}
        <div style={{ display: "flex", gap: "10px", marginBottom: "24px" }}>
          {ONBOARDING_SCHEMA.map((s, i) => {
            const active = i === step;
            const done = i < step && missingBySection[i].length === 0;
            const hasError = showErrors && missingBySection[i].length > 0;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => goTo(i)}
                style={{
                  flex: 1, textAlign: "left", cursor: "pointer",
                  padding: "12px 14px", borderRadius: "10px",
                  border: active ? "2px solid #161b22" : "1.5px solid #e0e4ea",
                  backgroundColor: active ? "#fff" : "#fafbfc",
                  boxShadow: active ? "0 2px 6px rgba(20,27,34,0.08)" : "none",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "3px" }}>
                  <span style={{
                    width: "22px", height: "22px", borderRadius: "6px", flexShrink: 0,
                    display: "grid", placeItems: "center", fontSize: "12px", fontWeight: 700,
                    backgroundColor: hasError ? "#c0392b" : done ? "#2e7d46" : active ? "#161b22" : "#c7cdd6",
                    color: "#fff",
                  }}>{done ? "✓" : i + 1}</span>
                  <span style={{ fontSize: "13.5px", fontWeight: 700, color: active ? "#161b22" : "#6b7480" }}>{s.label}</span>
                </div>
                {hasError && (
                  <div style={{ fontSize: "11px", color: "#c0392b", marginLeft: "30px" }}>
                    {missingBySection[i].length} required missing
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Section header */}
        <div style={{ marginBottom: "16px" }}>
          <h2 style={{ margin: "0 0 4px", fontSize: "19px", fontWeight: 700, color: "#161b22" }}>{section.title}</h2>
          <p style={{ margin: 0, color: "#6b7480", fontSize: "13.5px", maxWidth: "70ch" }}>{section.subtitle}</p>
        </div>

        {/* Validation banner */}
        {showErrors && sectionMissing.length > 0 && (
          <div style={{
            backgroundColor: "#fdecea", border: "1px solid #f5c6c0", color: "#a5281b",
            borderRadius: "8px", padding: "12px 14px", marginBottom: "16px", fontSize: "13px",
          }}>
            Please complete the required fields: <b>{sectionMissing.map((f) => f.label).join(", ")}</b>
          </div>
        )}

        {/* Save-error banner */}
        {saveError && (
          <div style={{
            backgroundColor: "#fdecea", border: "1px solid #f5c6c0", color: "#a5281b",
            borderRadius: "8px", padding: "12px 14px", marginBottom: "16px", fontSize: "13px",
          }}>
            {saveError}
          </div>
        )}

        {/* Boxes */}
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {section.boxes.map((box) => (
            <InvestorOnboardingBox
              key={box.title}
              box={box}
              form={form}
              setField={setField}
              neighborhoods={neighborhoods}
              teamMembers={teamMembers}
            />
          ))}
        </div>

        {/* Footer nav */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "28px" }}>
          <button
            type="button"
            onClick={() => goTo(step - 1)}
            disabled={step === 0}
            style={{
              padding: "11px 20px", borderRadius: "8px", fontSize: "14px", fontWeight: 600,
              border: "1.5px solid #d5dae1", backgroundColor: "#fff", color: step === 0 ? "#b8c0ca" : "#3d4753",
              cursor: step === 0 ? "not-allowed" : "pointer",
            }}
          >
            ← Back
          </button>

          <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
            {saved && !saveError && (
              <span style={{ color: "#2e7d46", fontSize: "13.5px", fontWeight: 600 }}>
                {isLast ? "✓ All sections saved" : `✓ Section ${step + 1} saved`}
              </span>
            )}
            {/* Sections 1 & 2 can be saved on their own for testing. */}
            {step < 2 && (
              <button
                type="button"
                onClick={() => (step === 0 ? saveProfile(false) : saveSearch(false))}
                disabled={saving}
                style={{ padding: "11px 20px", borderRadius: "8px", fontSize: "14px", fontWeight: 600, border: "1.5px solid #161b22", backgroundColor: "#fff", color: "#161b22", cursor: saving ? "wait" : "pointer" }}
              >
                {saving ? "Saving…" : `Save Section ${step + 1}`}
              </button>
            )}
            {!isLast ? (
              <button
                type="button"
                onClick={handleNext}
                disabled={saving}
                style={{ padding: "11px 24px", borderRadius: "8px", fontSize: "14px", fontWeight: 600, border: "none", backgroundColor: saving ? "#4a5058" : "#161b22", color: "#fff", cursor: saving ? "wait" : "pointer" }}
              >
                Save &amp; Next →
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={saving}
                style={{ padding: "11px 28px", borderRadius: "8px", fontSize: "14px", fontWeight: 600, border: "none", backgroundColor: saving ? "#4a5058" : "#161b22", color: "#fff", cursor: saving ? "wait" : "pointer" }}
              >
                {saving ? "Saving…" : "Finish"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
